export const injectHeadElements = (htmlString: string | undefined | null, targetDocument?: Document): void => {
  const doc = targetDocument || (typeof document !== 'undefined' ? document : null);
  if (!htmlString || !doc) return;

  // Remove HTML comments
  const cleanedHtml = htmlString.replace(/<!--[\s\S]*?-->/g, '');

  const parser = new DOMParser();
  const parsedDoc = parser.parseFromString(cleanedHtml, 'text/html');

  // Get all possible head elements
  const headElements = parsedDoc.querySelectorAll('script, link, meta, style, title, base');

  headElements.forEach(element => {
    try {
      const tagName = element.tagName.toLowerCase();

      // Check if element already exists to prevent duplicates
      if (tagName === 'script') {
        const scriptElement = element as HTMLScriptElement;
        const src = scriptElement.src;

        // For external scripts, check if already loaded by src
        if (src) {
          const existingScript = doc.querySelector(`script[src="${src}"]`);
          if (existingScript) {
            return; // Skip if already exists
          }
        } else {
          // For inline scripts, check by content or id
          const id = scriptElement.id;
          if (id) {
            const existingScript = doc.getElementById(id);
            if (existingScript) {
              return; // Skip if already exists
            }
          } else {
            // Check by content for inline scripts without id
            const content = scriptElement.textContent?.trim();
            if (content) {
              const existingScripts = doc.querySelectorAll('script:not([src])');
              for (const existing of Array.from(existingScripts)) {
                if (existing.textContent?.trim() === content) {
                  return; // Skip if same inline script already exists
                }
              }
            }
          }
        }
      } else if (tagName === 'link') {
        const linkElement = element as HTMLLinkElement;
        const href = linkElement.href;
        const rel = linkElement.rel;

        if (href) {
          const existingLink = doc.querySelector(`link[href="${href}"][rel="${rel}"]`);
          if (existingLink) {
            return; // Skip if already exists
          }
        }
      } else if (tagName === 'meta') {
        const metaElement = element as HTMLMetaElement;
        const name = metaElement.name;
        const property = metaElement.getAttribute('property');

        if (name) {
          const existingMeta = doc.querySelector(`meta[name="${name}"]`);
          if (existingMeta) {
            return; // Skip if already exists
          }
        } else if (property) {
          const existingMeta = doc.querySelector(`meta[property="${property}"]`);
          if (existingMeta) {
            return; // Skip if already exists
          }
        }
      }

      let newElement: HTMLElement;

      if (tagName === 'script') {
        // Special handling for scripts
        newElement = doc.createElement('script');

        // Copy all attributes
        Array.from(element.attributes).forEach(attr => {
          newElement.setAttribute(attr.name, attr.value);
        });

        // Use text property for inline scripts
        if (element.textContent && !(element as HTMLScriptElement).src) {
          (newElement as HTMLScriptElement).text = element.textContent;
        }

        // For external scripts, ensure they load
        if ((element as HTMLScriptElement).src) {
          newElement.setAttribute('crossorigin', 'anonymous');
        }
      } else {
        // Handle other head elements
        newElement = doc.createElement(element.tagName.toLowerCase());

        // Copy all attributes
        Array.from(element.attributes).forEach(attr => {
          newElement.setAttribute(attr.name, attr.value);
        });

        // Copy content for style and title tags
        if (element.tagName.toLowerCase() === 'style' || element.tagName.toLowerCase() === 'title') {
          newElement.textContent = element.textContent;
        }
      }

      doc.head.appendChild(newElement);
    } catch (error) {
      console.error(`Failed to inject ${element.tagName} element:`, error);
    }
  });
};

export const injectBodyElements = (htmlString: string | undefined | null, targetDocument?: Document): void => {
  const doc = targetDocument || (typeof document !== 'undefined' ? document : null);
  if (!htmlString || !doc) return;

  try {
    // Remove HTML comments
    const cleanedHtml = htmlString.replace(/<!--[\s\S]*?-->/g, '');

    const parser = new DOMParser();
    const parsedDoc = parser.parseFromString(cleanedHtml, 'text/html');

    // Get all body content
    const bodyContent = parsedDoc.body;

    if (bodyContent) {
      // Clone all child nodes from parsed body and append to target body
      while (bodyContent.firstChild) {
        const node = bodyContent.firstChild;

        const importedNode = doc.importNode(node, true);
        doc.body.appendChild(importedNode);
        bodyContent.removeChild(node);
      }
    }
  } catch (error) {
    console.error('Failed to inject body elements:', error);
  }
};
